import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { ZoomIn, ZoomOut, Maximize2, ArrowUpRight } from "lucide-react";
import {
  STATES,
  NATIONAL_AVG,
  STATE_LOAN_APR,
  NATIONAL_AVG_APR,
} from "../lib/states";
import { WARRANTY_STATES, NATIONAL_AVG_WARRANTY } from "../lib/warrantyStates";
import { track, events } from "../lib/analytics";

import { PAGE_CONTAINER } from "./PageContainer";
// Iter-147 — Warranty mode added. Lookup by state code for O(1) access
// in the color-grade + tooltip helpers. Mirrors STATE_LOAN_APR shape so
// the existing rendering loop stays clean.
const STATE_WARRANTY_AVG = WARRANTY_STATES.reduce((acc, s) => {
  acc[s.code] = s.avg_annual_cost;
  return acc;
}, {});

// CDN-hosted public US-Atlas topojson (TopoJSON-spec, ~70KB). Loaded once
// per page on mount; cached by the browser HTTP layer afterwards. This
// keeps the JS bundle small while delivering accurate Albers-USA
// geography (Alaska + Hawaii inset bottom-left). Pinned to v3 so a future
// us-atlas major bump can't silently break the rendering.
const TOPO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Map FIPS state IDs (as returned by us-atlas) → CarSavr's STATES dataset
// (which keys on USPS code). FIPS is the federal numeric ID; we look it
// up by state name as it appears in us-atlas. Pre-built once at module
// load so render is allocation-free.
const STATE_BY_NAME = STATES.reduce((acc, s) => {
  acc[s.name] = s;
  return acc;
}, {});

// Topojson FIPS-id → state-name fallback for the very few cases where
// us-atlas spells things differently than CarSavr's STATES list. Empty
// today; populate if a future build flags a mismatch in dev console.
const NAME_OVERRIDES = {};

/**
 * Premium clickable U.S. map. Used on:
 *   - /auto-insurance  → mode="insurance"  → click routes to /insurance/{slug}
 *   - /auto-loans      → mode="loans"      → click routes to /auto-loans/{slug}
 *   - /warranty        → mode="warranty"   → click routes to /warranty/{slug}
 *
 * Features:
 *   - Albers-USA projection (lower 48 + Alaska/Hawaii inset)
 *   - Color-graded fill by state insurance avg / loan APR / warranty cost
 *     vs. national avg (lower cost = paler green; higher = saturated)
 *   - Hover state with tooltip showing state name + key metric
 *   - Pan (mouse drag / touch drag) and zoom (+/− buttons, pinch on mobile)
 *   - Fully responsive — preserves aspect ratio at every breakpoint
 *   - Keyboard accessible — every state path is a focusable button
 *   - GA4 + PostHog click tracking
 *
 * @param {"insurance"|"loans"|"warranty"} mode
 * @param {string} [legend] - Optional override for the legend caption
 */
export default function InteractiveUSMap({ mode = "insurance", legend }) {
  const [states, setStates] = useState(null);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null); // { name, x, y, data }
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Albers USA projection sized for our 960×600 viewBox. Set the scale +
  // translate so the full continental US fits with comfortable padding.
  const projection = useMemo(
    () => geoAlbersUsa().scale(1200).translate([480, 280]),
    [],
  );
  const pathGen = useMemo(() => geoPath(projection), [projection]);

  useEffect(() => {
    let cancelled = false;
    fetch(TOPO_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`topo fetch failed: ${r.status}`);
        return r.json();
      })
      .then((topo) => {
        if (cancelled) return;
        // us-atlas v3 exposes states under topo.objects.states
        const geo = feature(topo, topo.objects.states);
        setStates(geo.features);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Color-grade fill — mode-aware. Insurance mode = green family by
  // ABSOLUTE annual full-coverage average (iter-185.W: switched from
  // ratio-vs-national to dollar-band thresholds so the map shading
  // matches the legend bands exactly per user audit feedback:
  // "Annual Full Coverage average color code does not match the
  // interactive map"). Loans mode + warranty mode unchanged (their
  // legends already render as descriptive ranges, not exact bands).
  const fillFor = (s) => {
    if (!s) return "#E2E8F0"; // unmatched state — slate-200 fallback
    if (mode === "loans") {
      const apr = STATE_LOAN_APR[s.code];
      if (apr == null) return "#E2E8F0";
      const delta = apr - NATIONAL_AVG_APR;
      if (delta <= -0.4) return "#D1FAE5";
      if (delta <= -0.1) return "#A7F3D0";
      if (delta <= 0.1) return "#6EE7B7";
      if (delta <= 0.4) return "#34D399";
      return "#10B981";
    }
    if (mode === "warranty") {
      const cost = STATE_WARRANTY_AVG[s.code];
      if (cost == null) return "#E2E8F0";
      const ratio = cost / NATIONAL_AVG_WARRANTY;
      if (ratio < 0.93) return "#D1FAE5";
      if (ratio < 0.98) return "#A7F3D0";
      if (ratio < 1.03) return "#6EE7B7";
      if (ratio < 1.10) return "#34D399";
      return "#10B981";
    }
    // Insurance: absolute-dollar bands aligned to the legend strip
    // shown beside the map. < $1.5k / $1.5–2k / $2–2.5k / $2.5–3k / > $3k.
    const avg = s.avg;
    if (avg < 1500) return "#D1FAE5";
    if (avg < 2000) return "#A7F3D0";
    if (avg < 2500) return "#6EE7B7";
    if (avg < 3000) return "#34D399";
    return "#10B981";
  };

  const handleClick = (s) => {
    if (!s) return;
    // Iter-76 — both modes now route to per-state landing pages.
    // Iter-147 — warranty mode also routes to per-state warranty landing
    // pages (which exist since iter-142 at /warranty/{slug}).
    // Iter-275.AA (Phase J) — use INTERNAL_NAV_CLICK, NOT AFFILIATE_CLICK.
    // Map-state clicks navigate to an internal route; tagging them as
    // affiliate clicks corrupts GA4's revenue funnel with non-revenue events.
    track(events.INTERNAL_NAV_CLICK, {
      kind: "us_map_state_click",
      mode,
      state_code: s.code,
      state_name: s.name,
    });
    if (mode === "loans") {
      navigate(`/auto-loans/${s.slug}`);
      return;
    }
    if (mode === "warranty") {
      navigate(`/warranty/${s.slug}`);
      return;
    }
    navigate(`/insurance/${s.slug}`);
  };

  const handleZoom = (direction) => {
    setScale((prev) => {
      const next = direction === "in" ? prev * 1.4 : prev / 1.4;
      return Math.min(4, Math.max(0.6, next));
    });
  };

  const handleReset = () => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  const onMouseDown = (e) => {
    setIsPanning(true);
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const onMouseMove = (e) => {
    if (!isPanning) return;
    setPan({
      x: e.clientX - panStart.current.x,
      y: e.clientY - panStart.current.y,
    });
  };

  const onMouseUp = () => setIsPanning(false);

  return (
    <section
      data-testid={`us-map-${mode}`}
      aria-label={
        mode === "loans"
          ? "Interactive U.S. auto-loan map"
          : mode === "warranty"
          ? "Interactive U.S. extended-warranty map"
          : "Interactive U.S. auto-insurance map"
      }
      className="border-y border-slate-200 bg-white"
    >
      <div className={`${PAGE_CONTAINER} py-16 lg:py-20`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#10B981] mb-3">
              {mode === "loans"
                ? "APR by state"
                : mode === "warranty"
                ? "Warranty cost by state"
                : "Rates by state"}
            </p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-slate-950 mb-4">
              {mode === "loans"
                ? "Find your state's average APR."
                : mode === "warranty"
                ? "See your state's warranty math."
                : "See how rates stack up."}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              {mode === "loans"
                ? "Auto-loan APRs vary 1.5+ points across states — local credit-union competition, lender density, captive-finance share, and per-state regulatory caps all swing the average. Click your state for the local APR plus lenders that actually quote there."
                : mode === "warranty"
                ? "Extended-warranty pricing varies by state because each state regulates Vehicle Service Contracts (VSCs) differently — some classify them as insurance, others as service contracts. Lemon-law windows, cancellation rules, and reserve-fund requirements all swing the average. Click your state for local pricing + the consumer-protection rules that apply."
                : "Where you live is the biggest single factor in your car insurance bill. Weather risk, no-fault statutes, uninsured-driver rates, and minimum coverage all swing the average. Click your state to see local averages and the fastest ways to cut your bill."}
            </p>

            {/* Color legend — premium fintech style. Iter-76: APR-aware
                ranges when mode=loans (delta from 7.3% national avg).
                Iter-147: warranty mode shows annual-cost ratio bands
                relative to the $1,580 national avg. */}
            <div className="border border-slate-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                {legend ||
                  (mode === "loans"
                    ? "Avg auto-loan APR (60-mo, 720+ FICO)"
                    : mode === "warranty"
                    ? "Avg annual VSC cost (5-yr midsize SUV)"
                    : "Annual full-coverage average")}
              </p>
              <div className="flex h-3 mb-2">
                {[
                  "#D1FAE5",
                  "#A7F3D0",
                  "#6EE7B7",
                  "#34D399",
                  "#10B981",
                ].map((c) => (
                  <div key={c} className="flex-1" style={{ background: c }} />
                ))}
              </div>
              {mode === "loans" ? (
                <div className="flex justify-between text-[10px] font-semibold text-slate-500">
                  <span>{"<"}6.9%</span>
                  <span>6.9–7.2%</span>
                  <span>7.2–7.4%</span>
                  <span>7.4–7.7%</span>
                  <span>{">"}7.7%</span>
                </div>
              ) : mode === "warranty" ? (
                <div className="flex justify-between text-[10px] font-semibold text-slate-500">
                  <span>{"<"}$1.47k</span>
                  <span>$1.47–1.55k</span>
                  <span>$1.55–1.63k</span>
                  <span>$1.63–1.74k</span>
                  <span>{">"}$1.74k</span>
                </div>
              ) : (
                <div className="flex justify-between text-[10px] font-semibold text-slate-500">
                  <span>{"<"}$1.5k</span>
                  <span>$1.5k–2k</span>
                  <span>$2k–2.5k</span>
                  <span>$2.5k–3k</span>
                  <span>{">"}$3k</span>
                </div>
              )}
            </div>

            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
              {mode === "loans"
                ? "Averages from Experian's State of the Auto Finance Market + aggregated credit-union surveys (Q4 2025). Tap any state to compare lender APRs in your local market."
                : mode === "warranty"
                ? "Averages from the 2024 Consumer Reports cross-state VSC survey, normalized to a 5-year-old midsize SUV at 60,000 miles. Tap any state for local pricing + state-specific consumer-protection rules."
                : "Averages from public DOI filings + aggregated quote data. Tap any state to see how to save in your local market."}
            </p>

            {/* Iter-76 — "View All States" deep-link below the legend.
                Both modes now ship per-state landing pages so the map can
                act as the visual entry-point AND the states hub the
                SEO-conscious explorer link.
                Iter-147 — warranty added. */}
            <Link
              to={
                mode === "loans"
                  ? "/auto-loans/states"
                  : mode === "warranty"
                  ? "/warranty#warranty-all-states"
                  : "/auto-insurance/states"
              }
              data-testid={`us-map-${mode}-view-all-states`}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-slate-950 hover:gap-2 transition-all underline-offset-2 hover:underline"
            >
              View 50 states + D.C.
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* MAP */}
          <div className="lg:col-span-8 relative">
            {/* Zoom controls (top-right of the map) */}
            <div className="absolute top-3 right-3 z-20 flex flex-col gap-1 bg-white border border-slate-200 shadow-sm">
              <button
                type="button"
                onClick={() => handleZoom("in")}
                data-testid="us-map-zoom-in"
                aria-label="Zoom in"
                className="w-11 h-11 grid place-items-center hover:bg-slate-50 border-b border-slate-200"
              >
                <ZoomIn className="w-4 h-4 text-slate-700" />
              </button>
              <button
                type="button"
                onClick={() => handleZoom("out")}
                data-testid="us-map-zoom-out"
                aria-label="Zoom out"
                className="w-11 h-11 grid place-items-center hover:bg-slate-50 border-b border-slate-200"
              >
                <ZoomOut className="w-4 h-4 text-slate-700" />
              </button>
              <button
                type="button"
                onClick={handleReset}
                data-testid="us-map-reset"
                aria-label="Reset map view"
                className="w-11 h-11 grid place-items-center hover:bg-slate-50"
              >
                <Maximize2 className="w-4 h-4 text-slate-700" />
              </button>
            </div>

            <div
              ref={wrapperRef}
              className={`border border-slate-200 bg-brand-surface-soft overflow-hidden select-none ${
                isPanning ? "cursor-grabbing" : "cursor-grab"
              }`}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={() => {
                onMouseUp();
                setHover(null);
              }}
              style={{ aspectRatio: "960 / 600" }}
            >
              {!states && !error && (
                <div
                  data-testid="us-map-loading"
                  className="w-full h-full grid place-items-center text-slate-500 text-sm"
                >
                  Loading map…
                </div>
              )}
              {error && (
                <div className="w-full h-full grid place-items-center text-rose-600 text-sm">
                  Map failed to load. Refresh the page to retry.
                </div>
              )}
              {states && (
                <svg
                  viewBox="0 0 960 600"
                  className="w-full h-full"
                  // Iter-275.Z (a11y) — SVG role removed. Previously the SVG
                  // declared role=image (atomic) which conflicts with its
                  // interactive `<path role="button">` children — axe flagged
                  // it as `nested-interactive`. Removing the role lets the
                  // browser treat the SVG as a generic graphics container
                  // (per SVG2's default `graphics-document` semantics); the
                  // `<title>` element still surfaces "Click a state to see…"
                  // as the SVG's accessible name to AT users, and each
                  // state path retains role=button + aria-label for
                  // keyboard / SR navigation.
                  aria-label={
                    mode === "loans"
                      ? "Interactive map — auto loan APRs by state"
                      : mode === "warranty"
                      ? "Interactive map — extended-warranty pricing by state"
                      : "Interactive map — auto insurance averages by state"
                  }
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                    transformOrigin: "center",
                    transition: isPanning ? "none" : "transform 0.2s ease",
                  }}
                >
                  <title id="us-map-title">
                    {mode === "loans"
                      ? "Click a state to see auto loan APRs"
                      : mode === "warranty"
                      ? "Click a state to see extended-warranty pricing"
                      : "Click a state to see auto insurance averages"}
                  </title>
                  {states.map((f) => {
                    const name =
                      NAME_OVERRIDES[f.id] || f.properties.name;
                    const s = STATE_BY_NAME[name];
                    const d = pathGen(f);
                    if (!d) return null;
                    const stateApr = s ? STATE_LOAN_APR[s.code] : null;
                    const stateWarranty = s ? STATE_WARRANTY_AVG[s.code] : null;
                    const ariaLabel = s
                      ? mode === "loans"
                        ? `${s.name} — average APR ${stateApr ?? NATIONAL_AVG_APR}%`
                        : mode === "warranty"
                        ? `${s.name} — average $${(stateWarranty ?? NATIONAL_AVG_WARRANTY).toLocaleString()}/yr warranty cost`
                        : `${s.name} — average $${s.avg.toLocaleString()}/yr`
                      : name;
                    return (
                      <path
                        key={f.id}
                        d={d}
                        data-testid={`us-map-state-${s?.code || f.id}`}
                        role="button"
                        aria-label={ariaLabel}
                        tabIndex={0}
                        fill={fillFor(s)}
                        stroke="#FFFFFF"
                        strokeWidth={1}
                        onMouseEnter={(e) => {
                          if (!s) return;
                          const rect = wrapperRef.current?.getBoundingClientRect();
                          if (!rect) return;
                          setHover({
                            state: s,
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                          });
                        }}
                        onMouseMove={(e) => {
                          if (!hover) return;
                          const rect = wrapperRef.current?.getBoundingClientRect();
                          if (!rect) return;
                          setHover((h) => ({
                            ...h,
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                          }));
                        }}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => handleClick(s)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleClick(s);
                          }
                        }}
                        className="cursor-pointer transition-colors hover:fill-[#0A2540] focus:outline-none focus-visible:fill-[#0A2540]"
                      />
                    );
                  })}
                </svg>
              )}

              {hover && (
                <div
                  role="tooltip"
                  data-testid={`us-map-tooltip-${mode}`}
                  className="absolute z-30 pointer-events-none bg-[#0A2540] text-white px-3 py-2 text-xs font-semibold shadow-lg"
                  style={{
                    left: Math.min(hover.x + 12, 700),
                    top: Math.max(0, hover.y - 60),
                  }}
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#10B981] mb-0.5">
                    {hover.state.name}
                  </p>
                  {mode === "loans" ? (
                    <>
                      <p className="font-display font-black text-lg leading-none mb-1">
                        {(STATE_LOAN_APR[hover.state.code] ?? NATIONAL_AVG_APR).toFixed(1)}
                        <span className="text-xs text-slate-500 font-bold">% APR</span>
                      </p>
                      <p className="text-[10px] text-slate-300">
                        {(() => {
                          const apr =
                            STATE_LOAN_APR[hover.state.code] ?? NATIONAL_AVG_APR;
                          const delta = apr - NATIONAL_AVG_APR;
                          if (Math.abs(delta) < 0.05) return "At the national average";
                          const pct = Math.abs(delta).toFixed(1);
                          return delta < 0
                            ? `${pct} pts below national avg`
                            : `${pct} pts above national avg`;
                        })()}
                      </p>
                      <p className="text-[10px] text-[#10B981] font-bold mt-1">
                        Click to compare lenders →
                      </p>
                    </>
                  ) : mode === "warranty" ? (
                    <>
                      <p className="font-display font-black text-lg leading-none mb-1">
                        ${(
                          STATE_WARRANTY_AVG[hover.state.code] ?? NATIONAL_AVG_WARRANTY
                        ).toLocaleString()}
                        <span className="text-xs text-slate-500 font-bold">/yr</span>
                      </p>
                      <p className="text-[10px] text-slate-300">
                        {(() => {
                          const cost =
                            STATE_WARRANTY_AVG[hover.state.code] ?? NATIONAL_AVG_WARRANTY;
                          const delta = cost - NATIONAL_AVG_WARRANTY;
                          if (Math.abs(delta) < 25) return "At the national average";
                          const pct = Math.abs(
                            Math.round((delta / NATIONAL_AVG_WARRANTY) * 100),
                          );
                          return delta < 0
                            ? `${pct}% below national avg`
                            : `${pct}% above national avg`;
                        })()}
                      </p>
                      <p className="text-[10px] text-[#10B981] font-bold mt-1">
                        Click to see warranty rules →
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-display font-black text-lg leading-none mb-1">
                        ${hover.state.avg.toLocaleString()}
                        <span className="text-xs text-slate-500 font-bold">/yr</span>
                      </p>
                      <p className="text-[10px] text-slate-300">
                        {hover.state.avg < NATIONAL_AVG
                          ? `${Math.round(((NATIONAL_AVG - hover.state.avg) / NATIONAL_AVG) * 100)}% under national avg`
                          : `${Math.round(((hover.state.avg - NATIONAL_AVG) / NATIONAL_AVG) * 100)}% above national avg`}
                      </p>
                      <p className="text-[10px] text-[#10B981] font-bold mt-1">
                        Click to see rates →
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            <p className="text-[11px] text-slate-500 mt-3 text-center">
              Click a state · drag to pan · use the controls to zoom
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
